import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Palette, Printer, Package, Globe, Gift, Image, TrendingUp, Award } from "lucide-react";
import "./Services.css";

import largeFormat    from '../../assets/images/largeFormatVid.mov';
import largeFormatImg from '../../assets/images/largeFormat.jpeg';

import bags1     from '../../assets/images/bags1.jpg';
import bags2     from '../../assets/images/bags2.jpeg';
import boxes     from '../../assets/images/boxes.jpeg';

import threeD1   from '../../assets/images/threeD2.jpeg';
import threeD2   from '../../assets/images/threeD.jpg';
import threeD3   from '../../assets/images/threeD1.jpg';

import brand1    from '../../assets/images/brand1.jpeg';
import brand2    from '../../assets/images/brand2.jpeg';
import brand3    from '../../assets/images/brand3.jpeg';
import brand4    from '../../assets/images/brand4.jpg';
import brand5    from '../../assets/images/brand5.jpg';

import brochure  from '../../assets/images/brochure.jpg';
import flyers    from '../../assets/images/flyers.jpg';
import gift1     from '../../assets/images/gift1.jpg';
import bdCard    from '../../assets/images/bdCard.jpg';

import tshirt    from '../../assets/images/tshirt.jpg';
import tshirt2   from '../../assets/images/tshirtsPrinting.jpg';
import wristband from '../../assets/images/wristband.jpg';
import reflectors from '../../assets/images/reflectors.jpg';
import lanyard   from '../../assets/images/lanyard.jpg';
import caps      from '../../assets/images/Caps.jpg';

import book1     from '../../assets/images/book1.jpeg';
import book2     from '../../assets/images/book2.jpeg';
import book3     from '../../assets/images/book3.jpeg';
import book4     from '../../assets/images/book4.jpg';

const R = "#FF2B2B", S1 = "#141414";

// Maps each card label → the :serviceId used in ServiceDetails.jsx
const ROUTE_ID = {
  "3D Signage":          "3d-signage",
  "Web Design & Dev":    "web-design",
  "Brand Identity":      "branding",
  "Printing Services":   "printing",
  "Packaging Solutions": "packaging",
  "Promotional Merch":   "merchandise",
  "Large Format Print":  "large-format",
  "Book Printing":       "book-printing",
};

const SERVICES = [
  {
    Icon: Box, label: "3D Signage", cat: "Signage", span: "c7 r3", large: true, tag: "Popular",
    imgs: [threeD3, threeD2, threeD1],
    items: ["Shop & Fascia Signs", "Illuminated Channel Letters", "Reception Signs", "Wayfinding Systems"],
    desc: "Make your brand impossible to ignore with premium fabricated and illuminated signage.",
  },
  {
    Icon: Globe, label: "Web Design & Dev", cat: "Digital", span: "c5 r3", large: true, tagDark: true,
    imgs: [
      "https://images.unsplash.com/photo-1547658719-da2b51169166?w=700&q=80",
      "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=700&q=80",
      "https://images.unsplash.com/photo-1558655146-d09347e92766?w=700&q=80",
    ],
    items: ["Responsive Design", "SEO Optimised", "E-commerce Ready", "Fast & Secure"],
    desc: "Modern, fast websites built to convert visitors into customers.",
  },
  {
    Icon: Palette, label: "Brand Identity", cat: "Branding", span: "c3 r2",
    imgs: [brand1, brand2, brand3, brand4, brand5],
    desc: "Logos, brand guides, stationery & social kits.",
  },
  {
    Icon: Printer, label: "Printing Services", cat: "Print", span: "c3 r2",
    imgs: [brochure, flyers, gift1, bdCard],
    desc: "Business cards, flyers, brochures & posters.",
  },
  {
    Icon: Package, label: "Packaging Solutions", cat: "Packaging", span: "c3 r2",
    imgs: [bags2, bags1, boxes],
    desc: "Branded bags, boxes & product labels.",
  },
  {
    Icon: Gift, label: "Promotional Merch", cat: "Merchandise", span: "c3 r2",
    imgs: [tshirt, tshirt2, wristband, reflectors, lanyard, caps],
    desc: "Mugs, t-shirts, caps & gift sets.",
  },
  {
    Icon: Image, label: "Large Format Print", cat: "Large Format", span: "c4 r2",
    img: largeFormatImg,
    video: largeFormat,
    desc: "Banners, rollups & vehicle wraps.",
  },
  {
    Icon: TrendingUp, label: "Book Printing", cat: "Publishing", span: "c4 r2",
    imgs: [book1, book2, book3, book4],
    desc: "Digital Printing, offset, publishing.",
  },
];

function SvcCard({ s }) {
  const [idx, setIdx] = useState(0);
  const timer         = useRef(null);
  const vidRef        = useRef(null);
  const navigate      = useNavigate();
  const imgs          = s.imgs || (s.img ? [s.img] : []);

  const enter = () => {
    if (s.video) { vidRef.current?.play().catch(() => {}); return; }
    if (imgs.length < 2) return;
    clearInterval(timer.current);
    timer.current = setInterval(() => setIdx(i => (i + 1) % imgs.length), 1500);
  };

  const leave = () => {
    if (s.video) {
      const v = vidRef.current;
      if (v) { v.pause(); v.currentTime = 0; }
      return;
    }
    clearInterval(timer.current);
    setIdx(0);
  };

  useEffect(() => () => clearInterval(timer.current), []);

  const handleClick = () => {
    const id = ROUTE_ID[s.label];
    if (id) {
      navigate(`/services/${id}`);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <div
      className={`svc ${s.span}`}
      onMouseEnter={enter}
      onMouseLeave={leave}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      aria-label={`View ${s.label} service details`}
      onKeyDown={e => e.key === "Enter" && handleClick()}
      style={{ cursor: "pointer" }}
    >
      <div className="bgs">
        {s.video ? (
          <video
            ref={vidRef}
            className="vid"
            src={s.video}
            poster={s.img}
            muted loop playsInline preload="metadata"
          />
        ) : (
          imgs.map((src, i) => (
            <div
              key={i}
              className="bg"
              style={{ backgroundImage: `url(${src})`, opacity: i === idx ? 1 : 0 }}
            />
          ))
        )}
      </div>

      <div className={s.large ? "ovL" : "ovB"} />

      {s.tag && (
        <div
          className="tag"
          style={s.tagDark ? { background: S1, color: R, border: "1px solid rgba(255,43,43,.4)" } : {}}
        >
          {s.tag}
        </div>
      )}

      <div className="body">
        <div className="ibox"><s.Icon size={18} color={R} strokeWidth={1.6} /></div>
        <div className="cat">{s.cat}</div>
        <div className="name">{s.label}</div>
        {s.desc  && <p className="desc">{s.desc}</p>}
        {s.items && <ul>{s.items.map(i => <li key={i}>{i}</li>)}</ul>}
      </div>
    </div>
  );
}

export default function Services() {
  return (
    <section className="svc-section" aria-label="Services">
      <div className="head">
        <div className="eyebrow"><i />What We Do</div>
        <h2>Eight Ways We Make You Unforgettable</h2>
        <p className="sub">One studio, every discipline — everything your brand needs to dominate.</p>
      </div>

      <div className="bento">
        {SERVICES.map(s => <SvcCard key={s.label} s={s} />)}
        <div className="stat c4 r2">
          <div className="glow" />
          <Award size={20} color={R} strokeWidth={1.5} style={{ position: "relative" }} />
          <div>
            <div className="n">98%</div>
            <div className="l">Client Satisfaction</div>
          </div>
        </div>
      </div>
    </section>
  );
}